import React, { useCallback, useMemo, useRef, useState } from 'react';
import { VisitableType } from '@enums/visitable-type-enum';
import { flattenOpacity } from '@utils/colors';
import { EventState } from '../../Enums/event-state-enum';
import ResizeHandles from './ResizeHandles';
import useEventContext from '../scheduler-only-hooks/use-event-context';
function EventVisit() {
    var _a, _b;
    const { event } = useEventContext();
    const { clusterInfo, state, metadata, handleMouseDown } = useEventContext();
    const eventCardRef = useRef(null);
    const buildingInfoRef = useRef(null);
    const [buildingInfoStyle, setBuildingInfoStyle] = useState({});
    const secondaryInfoRef = useRef(null);
    const [secondaryInfoStyle, setSecondaryInfoStyle] = useState({});
    const dynamicStyles = useMemo(() => {
        return {
            left: `${metadata.startTimeAsPercentage}%`,
            width: `${metadata.endTimeAsPercentage - metadata.startTimeAsPercentage}%`,
            height: `${(100 / clusterInfo.subdivision) * clusterInfo.span}%`,
            top: `calc((${100 / clusterInfo.subdivision}% ) * ${clusterInfo.offset})`,
            borderTop: clusterInfo.offset === 0 ? 'unset' : 'transparent',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '--custom-status-color': `${event.status.color}4D`
        };
    }, [event, clusterInfo, metadata]);
    const dynamicClass = useMemo(() => {
        const classes = ['event__item--custom'];
        if (state === EventState.REPOSITIONING)
            classes.push('--dragging');
        return classes.join('');
    }, [state]);
    const expandedStyle = useCallback((div, pinTop = false) => {
        const eventDiv = eventCardRef.current;
        if (!div || !eventDiv)
            return null;
        const divRect = div.getBoundingClientRect();
        const eventDivRect = eventDiv.getBoundingClientRect();
        return Object.assign(Object.assign({ left: `${divRect.left - eventDivRect.left - 6}px` }, (pinTop ? { top: `${divRect.top - eventDivRect.top - 4}px` } : {})), { zIndex: 50, position: 'absolute', minWidth: 'max-content', maxWidth: 'none', background: '#fff', padding: '0 2px', borderRadius: '4px', border: `4px solid ${flattenOpacity(`${event.status.color}4D`)}` });
    }, [event.status.color]);
    const handleBuildingMouseEnter = useCallback(() => {
        const style = expandedStyle(buildingInfoRef.current);
        if (style)
            setBuildingInfoStyle(style);
    }, [expandedStyle]);
    const handleBuildingMouseLeave = useCallback(() => {
        setBuildingInfoStyle({});
    }, []);
    const handleSecondaryMouseEnter = useCallback(() => {
        const style = expandedStyle(secondaryInfoRef.current, true);
        if (style)
            setSecondaryInfoStyle(style);
    }, [expandedStyle]);
    const handleSecondaryMouseLeave = useCallback(() => {
        setSecondaryInfoStyle({});
    }, []);
    const descriptionStyle = useMemo(() => (secondaryInfoStyle.position ? { maxWidth: 'none' } : {}), [secondaryInfoStyle]);
    return (React.createElement("div", { role: "presentation", className: dynamicClass, style: dynamicStyles, onMouseDown: () => handleMouseDown(), ref: eventCardRef },
        React.createElement("div", { className: "event__card" },
            React.createElement(ResizeHandles, null),
            React.createElement("div", { className: "event__prefix" },
                React.createElement("div", { className: "event__indicator", style: { background: event.status.color } })),
            React.createElement("div", { className: "event__body" },
                React.createElement("div", { className: "event__primary" },
                    React.createElement("span", { className: "event__type" },
                        React.createElement(Icon, { type: event.visitable.type })),
                    React.createElement("span", { className: "event__time" }, event.start.format('h:mma')),
                    React.createElement("span", { className: "event__bullet_wrapper" },
                        React.createElement("span", { className: "event__bullet" })),
                    React.createElement("div", { className: "event__building", style: buildingInfoStyle, ref: buildingInfoRef, onMouseEnter: handleBuildingMouseEnter, onMouseLeave: handleBuildingMouseLeave },
                        React.createElement("span", { className: "event__building_name" }, (_a = event.building) === null || _a === void 0 ? void 0 : _a.name),
                        React.createElement("span", { className: "event__building_address" }, (_b = event.building) === null || _b === void 0 ? void 0 : _b.address))),
                React.createElement("div", { className: "event__secondary", style: secondaryInfoStyle, ref: secondaryInfoRef, onMouseEnter: handleSecondaryMouseEnter, onMouseLeave: handleSecondaryMouseLeave },
                    event.referenceNumber && React.createElement("span", { className: "event__reference" },
                        "Ref# ",
                        event.referenceNumber),
                    React.createElement("span", { className: "event__description", style: descriptionStyle }, event.description))))));
}
function Icon({ type }) {
    switch (type) {
        case VisitableType.SERVICE:
            return React.createElement("i", { className: "fa-light fa-screwdriver-wrench" });
        case VisitableType.INSPECTION:
            return React.createElement("i", { className: "fa-light fa-clipboard-check" });
        default:
            return React.createElement("i", { className: "fa-light fa-square-question" });
    }
}
export default EventVisit;
